import React, { useEffect, useState } from "react";
import GradientButton from "../../components/button/Button";
import PlainButton from "../../components/button/PlainButton";
import { Cam } from "../../images/Images";
import { ToggleOnIcon } from "../../components/icons/Icons";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import LoadingComponent from "../../components/loading/Loading";
import useAuth from "../../state/useAuth";
import type { AxiosProgressEvent } from "axios";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProductSegment {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  description: string;
  image: string;
  threshold: string;
}

interface Venture {
  name: string;
  id: string;
}

const StockAddModal: React.FC<FilterModalProps> = ({ isOpen, onClose }) => {
  const { id } = useAuth();

  const [filters, setFilters] = useState({
    name: "",
    category: "",
    productDesc: "",
    amount: "",
    stockQuantity: 100,
    vendor: id,
    tags: "",
    threshold: "",
    image: null as File | null,
  });

  const [segments, setSegments] = useState<ProductSegment[]>([]);
  const [ventures, setVentures] = useState<Venture[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | undefined>(
    undefined
  );
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [thresholdError, setThresholdError] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    getCategories();
    // getVentures();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [name]: value,
      };

      if (name === "threshold") {
        setThresholdError(Number(value) < 50);
        setErrors((prev) => ({
          ...prev,
          stockQuantity:
            Number(updatedFilters.stockQuantity) < Number(value)
              ? "Stock Quantity must not be less than Restock Alert."
              : "",
          threshold:
            Number(value) < 50 ? "Restock Alert must not be less than 50." : "",
        }));
      }
      if (name === "stockQuantity") {
        setErrors((prev) => ({
          ...prev,
          stockQuantity:
            Number(value) < Number(updatedFilters.threshold)
              ? "Stock Quantity must not be less than Restock Alert."
              : "",
        }));
      }

      return updatedFilters;
    });
  };
  const getCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axiosInstance.get("/segments");
      if (res?.data) {
        setSegments(res.data?.data || []);
      }
      setLoadingCategories(false);
    } catch (error) {
      setLoadingCategories(false);
      console.error("Error fetching categories:", error);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setFilters((prevFilters) => ({
        ...prevFilters,
        image: file,
      }));

      // Create an image preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  // Validate all fields before submit
  const validateInputs = () => {
    const newErrors: { [key: string]: string } = {};
    if (!filters.name.trim()) newErrors.name = "Product name is required.";
    if (!filters.category) newErrors.category = "Category is required.";
    if (!filters.amount || isNaN(Number(filters.amount)))
      newErrors.amount = "Valid product amount is required.";
    if (
      !filters.stockQuantity ||
      Number(filters.stockQuantity) < Number(filters.threshold)
    )
      newErrors.stockQuantity =
        "Stock Quantity must not be less than Restock Alert.";
    if (!filters.threshold || Number(filters.threshold) < 50)
      newErrors.threshold = "Restock Alert must not be less than 50.";
    if (!filters.tags.trim()) newErrors.tags = "Product tags are required.";
    if (!filters.productDesc.trim())
      newErrors.productDesc = "Product description is required.";
    if (!filters.image) newErrors.image = "Product image is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateInputs()) {
      toast.error("Please fix the errors before submitting.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", filters.name);
    formData.append("description", filters.productDesc);
    formData.append("amount", filters.amount);
    formData.append("unit", "per kg");
    formData.append("tags", filters.tags);
    formData.append("segment", filters.category);
    formData.append("stock_count", String(filters.stockQuantity));
    formData.append("threshold", String(filters.threshold));
    if (filters.image instanceof File) {
      formData.append("image", filters.image);
    }

    try {
      const res = await axiosInstance.post("/products", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadPercent(percentCompleted);
          }
        },
      });

      if (res.data) {
        toast.success("Product added successfully!");
        setFilters({
          name: "",
          category: "",
          productDesc: "",
          amount: "",
          stockQuantity: 100,
          vendor: "",
          tags: "",
          image: null,
          threshold: "",
        });
        setImagePreview(null);
        setLoading(false);
        onClose();
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Something went wrong");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // if (loading) {
  //   return <LoadingComponent percent={uploadPercent} />;
  // }

  const handleFilter = () => {
    console.log("Filters on Apply:", filters);
    onClose();
  };

  return (
    <div className="orders-modal">
      <div className="orders-modal__overlay" onClick={onClose}></div>
      <div
        className="orders-modal__content"
        style={{ height: "570px", overflowY: "scroll" }}
      >
        <div className="orders__modal__header">
          <h2 className="orders-modal__title">Add Product</h2>
          <button className="orders-modal__close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form
          className="orders-modal__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Product Name */}
          <div className="orders-modal__form-group">
            <label htmlFor="name">Product Name</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Enter product name"
              value={filters.name}
              onChange={handleChange}
            />
            {errors.name && <div className="input-error">{errors.name}</div>}
          </div>

          {/* Category */}
          <div className="orders-modal__form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={filters.category}
              onChange={handleChange}
            >
              <option value="">Select Category</option>
              {loadingCategories ? (
                <option disabled>Loading categories...</option>
              ) : (
                segments.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))
              )}
            </select>
            {errors.category && (
              <div className="input-error">{errors.category}</div>
            )}
          </div>

          {/* Product Amount */}
          <div className="orders-modal__form-group">
            <label htmlFor="amount">Product Amount</label>
            <input
              type="text"
              id="amount"
              name="amount"
              value={filters.amount}
              onChange={handleChange}
              placeholder="E.g. $150"
            />
            {errors.amount && (
              <div className="input-error">{errors.amount}</div>
            )}
          </div>

          {/* Stock Quantity */}
          <div className="orders-modal__form-group">
            <label htmlFor="stockQuantity">Stock Quantity</label>
            <input
              type="number"
              id="stockQuantity"
              name="stockQuantity"
              placeholder="E.g 100"
              value={filters.stockQuantity}
              onChange={handleChange}
              min={50}
            />
            {errors.stockQuantity && (
              <div className="input-error">{errors.stockQuantity}</div>
            )}
          </div>

          {/* Restock Alert */}
          <div className="orders-modal__form-group">
            <label htmlFor="threshold">Restock Alert</label>
            <input
              type="number"
              id="threshold"
              name="threshold"
              placeholder="min 50"
              value={filters.threshold}
              onChange={handleChange}
              min={50}
              style={thresholdError ? { borderColor: "#f44336" } : {}}
            />
            {thresholdError && (
              <div style={{ color: "#f44336", fontSize: 13, marginTop: 2 }}>
                Value must not be less than 50
              </div>
            )}
            {errors.threshold && (
              <div className="input-error">{errors.threshold}</div>
            )}
          </div>

          {/* Tags */}
          <div className="orders-modal__form-group">
            <label htmlFor="tags">Product Tags</label>
            <input
              type="text"
              id="tags"
              name="tags"
              placeholder="E.g flakes,cereal"
              value={filters.tags}
              onChange={handleChange}
            />
            {errors.tags && <div className="input-error">{errors.tags}</div>}
          </div>

          {/* Product Description */}
          <div className="orders-modal__form-group" style={{ width: "100%" }}>
            <label htmlFor="productDesc">Product Description</label>
            <textarea
              id="productDesc"
              name="productDesc"
              placeholder="E.g. Yam Flour is made from Yam"
              value={filters.productDesc}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: 4,
                resize: "none",
                outline: "none",
                borderRadius: 5,
                border: "1px solid #ccc",
                height: 100,
              }}
            />
            {errors.productDesc && (
              <div className="input-error">{errors.productDesc}</div>
            )}
          </div>

          {/* Image Upload */}
          <div
            className="orders-modal__form-group form__upload"
            style={{ width: "100%" }}
          >
            <input
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              id="imageUpload"
              onChange={handleImageChange}
            />
            <label
              htmlFor="imageUpload"
              style={{ textAlign: "center", cursor: "pointer" }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 5,
                    objectFit: "cover",
                  }}
                />
              ) : (
                <>
                  <img
                    src={Cam}
                    alt="Upload"
                    style={{ width: 25, height: 25 }}
                  />
                  <p>Upload Image</p>
                  <p style={{ color: "#7E7E7E" }}>JPG and PNG, less than 1MB</p>
                </>
              )}
            </label>
            {errors.image && <div className="input-error">{errors.image}</div>}
          </div>

          {/* Actions */}
          <div className="orders-modal__actions stock">
            <GradientButton
              onClick={handleFilter}
              text="Save As Draft"
              disabled={loading}
            />
            <PlainButton
              onClick={handleSubmit}
              text={
                loading ? (
                  <span>
                    <span className="loading-spinner" /> Submitting...
                  </span>
                ) : (
                  "Submit"
                )
              }
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAddModal;
